import LazyImage from 'components/Image/LazyImage';
import noImage from 'components/Image/noIMG.svg';
import QuantityInput from 'components/MarketItem/QuantityInput/QuantityInput';
import { formatVND } from 'containers/QuickOrder/utils';
import useProductUomInfo from 'hooks/useProductUomInfo';
import React, { memo, useCallback, useRef, useState } from 'react';
import styled from 'styles/styled-components';
import translations from 'translations';
import { AiMatchedProduct, TrackingAddToCartAddFromType, Uom } from 'types/schema';
import AccurateBadge from '../AccurateBadge';
import { RefreshIcon, SearchIcon } from '../icons';
import messages from '../messages';
import InlineSearch from './InlineSearch';
import { ResultRowProps } from './types';

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px 8px;
  background: #fff;
  border-radius: 8px;
`;

const Top = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  font-size: 10px;
  line-height: 14px;
`;

const Prefix = styled.span`
  font-weight: 400;
  color: #4b5563;
`;

const Quoted = styled.span`
  font-weight: 400;
  color: #4b5563;
`;

const DotWrap = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0 4px;
  font-size: 10px;
  line-height: 14px;
  color: #9ca3af;
`;

const Divider = styled.div`
  height: 1px;
  background: #f2f3f5;
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const Checkbox = styled.button<{ checked: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1px solid ${({ checked }) => (checked ? '#43A047' : '#D1D5DB')};
  background: ${({ checked }) => (checked ? '#43A047' : '#fff')};
  cursor: pointer;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  align-self: center;

  &::after {
    content: '';
    display: ${({ checked }) => (checked ? 'block' : 'none')};
    width: 4px;
    height: 8px;
    border: solid #fff;
    border-width: 0 1.5px 1.5px 0;
    transform: rotate(45deg) translate(-1px, -1px);
  }
`;

const ContentRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const ThumbWrap = styled.div`
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  align-self: center;
`;

const Thumb = styled(({ noImage: _noImage, ...rest }) => <LazyImage {...rest} />)<{ noImage: boolean }>`
  width: 48px;
  height: 48px;
  border-radius: 4px;
  object-fit: ${({ noImage: noImg }) => (noImg ? 'contain' : 'cover')};
  ${({ noImage: noImg }) => (noImg ? 'opacity: 0.3;' : '')}
  background: #f5f5f5;
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const InfoTop = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Name = styled.div`
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #111827;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const UnitRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Unit = styled.span`
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #4b5563;
`;

const PurchasedTag = styled.span`
  display: inline-flex;
  align-items: center;
  height: 14px;
  padding: 0 4px;
  border-radius: 4px;
  background: #f2f3f5;
  color: #303643;
  font-size: 10px;
  font-weight: 500;
  line-height: 14px;
`;

const Price = styled.div`
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  color: #111827;
`;

const ChangeBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #6b7280;
  font-size: 10px;
  font-weight: 400;
  line-height: 14px;
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover {
    opacity: 0.8;
  }
`;

const SearchManuallyBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #303643;
  font-size: 10px;
  font-weight: 400;
  line-height: 14px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  white-space: nowrap;

  &:hover {
    opacity: 0.8;
  }
`;

const UnmatchedTop = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 10px;
  line-height: 14px;
`;

const UnmatchedLeft = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
`;

const NotFoundText = styled.span`
  color: #e2483c;
  font-size: 10px;
  font-weight: 400;
  line-height: 14px;
`;

const SearchPanel = styled.div`
  margin-top: 8px;
`;

const UserInputText = styled.span`
  font-size: 10px;
  font-weight: 400;
  line-height: 14px;
  color: #6b7280;
`;

const QuantityCol = styled.div`
  flex-shrink: 0;
  align-self: flex-start;
`;

const TopMeta = ({ inputText, level }: { inputText: string; level: string }) => (
  <Top>
    <Prefix>{translations(messages.userInputPrefix)} </Prefix>
    <Quoted>{`"${inputText}"`}</Quoted>
    <DotWrap>•</DotWrap>
    <AccurateBadge level={level} variant="inline" />
  </Top>
);

const UnmatchedRow = ({ product, onSwapProduct }: Pick<ResultRowProps, 'product' | 'onToggle' | 'onSwapProduct'>) => {
  const [isSearching, setIsSearching] = useState(false);
  const handleSelect = useCallback(
    (alt: AiMatchedProduct) => onSwapProduct(product.id, alt),
    [product.id, onSwapProduct],
  );

  return (
    <Card style={{ padding: '12px 8px' }}>
      <UnmatchedTop>
        <UnmatchedLeft>
          <Prefix>{translations(messages.userInputPrefix)} </Prefix>
          <Quoted>{`"${product.inputText}"`}</Quoted>
          <DotWrap>•</DotWrap>
          <NotFoundText>{translations(messages.notFound)}</NotFoundText>
        </UnmatchedLeft>
        <SearchManuallyBtn type="button" onClick={() => setIsSearching(true)}>
          <SearchIcon size={12} color="#303643" />
          {translations(messages.searchManually)}
        </SearchManuallyBtn>
      </UnmatchedTop>
      {isSearching && (
        <SearchPanel>
          <InlineSearch onSelect={handleSelect} onClose={() => setIsSearching(false)} />
        </SearchPanel>
      )}
    </Card>
  );
};

const MatchedRow = ({
  product,
  quantity,
  onToggle,
  onQuantityChange,
  onChangeProduct,
  loggedIn,
  goToSignIn,
}: Omit<ResultRowProps, 'onSwapProduct'>) => {
  const p = product.matchedProduct ?? null;
  const changeBtnRef = useRef<HTMLButtonElement>(null);
  const { uomInfo } = useProductUomInfo(p ?? ({} as AiMatchedProduct));

  const handleToggle = useCallback(() => onToggle(product.id), [product.id, onToggle]);
  const handleChangeProduct = useCallback(() => {
    if (changeBtnRef.current) onChangeProduct(product.id, product.inputText, changeBtnRef.current);
  }, [product.id, product.inputText, onChangeProduct]);
  const handleQuantity = useCallback(
    (value: number) => onQuantityChange(product.id, value),
    [product.id, onQuantityChange],
  );

  if (!p) return null;

  const isKg = [Uom.Kilogram].includes(p.uom as Uom);

  return (
    <Card>
      <TopMeta inputText={product.inputText} level={product.confidence} />
      <Divider />
      <Row>
        <Checkbox checked={product.selected} onClick={handleToggle} aria-label="select" />
        <ContentRow>
          <ThumbWrap>
            <Thumb noImage={!p.imageUrl} src={p.imageUrl || noImage} alt={p.name} />
          </ThumbWrap>
          <Info>
            <InfoTop>
              <Name>{p.name}</Name>
              <UnitRow>
                <Unit>{uomInfo}</Unit>
                {product.fromHistory && <PurchasedTag>{translations(messages.purchasedTag)}</PurchasedTag>}
              </UnitRow>
              <Price>{formatVND(p.price)}</Price>
            </InfoTop>
            <ChangeBtn ref={changeBtnRef} type="button" onClick={handleChangeProduct}>
              <RefreshIcon />
              {translations(messages.changeProduct)}
            </ChangeBtn>
          </Info>
          <QuantityCol>
            <QuantityInput
              loggedIn={loggedIn}
              productId={p.sku}
              min={isKg ? 0 : 1}
              max={999}
              value={quantity}
              onChange={handleQuantity}
              allowDecimal={isKg}
              orderDisabled={false}
              goToSignIn={goToSignIn}
              addFrom={TrackingAddToCartAddFromType.AllProduct}
              isShowTotalMoney={false}
            />
          </QuantityCol>
        </ContentRow>
      </Row>
    </Card>
  );
};

const ResultRowMobile = (props: ResultRowProps) => {
  if (!props.product.matchedProduct) {
    return <UnmatchedRow product={props.product} onToggle={props.onToggle} onSwapProduct={props.onSwapProduct} />;
  }
  return <MatchedRow {...props} />;
};

export default memo(ResultRowMobile);
